import ReportEmail from './ReportEmail';
import React, { useState, useEffect, useCallback } from 'react';
import { ordenarRefeicoes } from '../PlanoAlimentar/helpers';
import ReactDOM from 'react-dom';
import { reportsService, recommendationsService, appointmentsService, bioimpedanciaService, anthropometryService } from '../../services/api';
import CorpoAnotado, { medidasDoCorpo, fmtBR } from './CorpoAnotado';
import { classifyIMC, classifyGordura, classifyVisceral, classifyMuscle, classifyCorpAge } from '../Bioimpedancia/PrintEvolution';
import { Ico, emo } from '../ui/Ico';
import { useMarca, LOGO_LEVNUTRI, reportLogoStyle } from '../../branding';

// ─── Constantes de design ──────────────────────────────────────────────────────
const GOLD     = '#d4a574';
const GOLD_DK  = '#9a6a42';
const CREAM    = '#fbf8f5';
const CREAM2   = '#f5ece2';
const TEXT     = '#2f2a26';
const TEXT_LT  = '#736b64';
const DOC_FONT = "'Georgia', 'Times New Roman', serif";
const UI_FONT  = "'Nunito', sans-serif";

const MEAL_LABELS = {
  cafe_manha:'Café da Manhã', lanche_manha:'Lanche da Manhã',
  almoco:'Almoço', lanche_tarde:'Lanche da Tarde',
  jantar:'Jantar', ceia:'Ceia', pre_treino:'Pré-Treino', pos_treino:'Pós-Treino',
};

// Seções configuráveis
const SECTION_DEFS = [
  { key: 'refeicoes',     label: 'Plano Alimentar',             desc: 'Refeições do dia + metas de macronutrientes', group: 'plano' },
  { key: 'suplementacao', label: 'Suplementação prescrita',     desc: 'Suplementos definidos no plano alimentar',    group: 'plano' },
  { key: 'orientacoes',   label: 'Orientações personalizadas',  desc: 'Diretrizes e dicas individuais',              group: 'plano' },
  { key: 'agua',          label: 'Meta de hidratação',          desc: 'Consumo diário de água recomendado',          group: 'rec'   },
  { key: 'alimentos',     label: 'Alimentos a evitar',          desc: 'Lista de alimentos não recomendados',         group: 'rec'   },
  { key: 'suplementos',   label: 'Suplementos e fitoterápicos', desc: 'Tabela de suplementação das recomendações',   group: 'rec'   },
  { key: 'atividade',     label: 'Atividade física',            desc: 'Tipo, frequência e intensidade sugerida',     group: 'rec'   },
  { key: 'outras',        label: 'Outras recomendações',        desc: 'Notas e orientações adicionais',              group: 'rec'   },
  { key: 'proxima',       label: 'Próxima consulta agendada',   desc: 'Data e horário do próximo retorno',           group: 'extra' },
  { key: 'evolucao',     label: '📈 Evolução do Tratamento',   desc: 'Tabela de medições + mensagem motivacional',  group: 'extra' },
  { key: 'composicao',   label: 'Composição corporal',         desc: 'Bioimpedância na figura: gordura, músculo, IMC e mais', group: 'corpo' },
  { key: 'medidas',      label: 'Medidas',                     desc: 'Antropometria: circunferências na figura, RCQ e dobras', group: 'corpo' },
];

const DEFAULT_SECTIONS = {
  refeicoes: true, suplementacao: true, orientacoes: true,
  agua: true, alimentos: true, suplementos: true, atividade: true, outras: true,
  proxima: false, evolucao: false,
  composicao: false, medidas: false, // ligadas automaticamente quando há dados
};

function fmtNum(v, d = 1) {
  const n = parseFloat(v);
  return isNaN(n) ? '-' : n.toFixed(d);
}

function mealList(meals) {
  if (!meals) return [];
  return ordenarRefeicoes(meals)
    .filter(([, m]) => m.alimentos?.length > 0)
    .map(([k, m]) => ({ ...m, _key: k }));
}

function normText(str) {
  if (!str) return '';
  return String(str).replace(/\\n/g, '\n');
}

function fmtDateTime(str) {
  if (!str) return '';
  const d = new Date(String(str).replace(' ', 'T'));
  return d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
    + ' às ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

// ─── Print CSS ─────────────────────────────────────────────────────────────────
function DocCss() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;500;600;700;800&display=swap');

      @media screen  { .lev-relatorio-print { display: none !important; } }

      @media print {
        body > *:not(.lev-relatorio-print) { display: none !important; }
        body > .lev-relatorio-print        { display: block !important; }

        @page { size: A4 portrait; margin: 1.5cm 1.8cm; }

        .ndoc-nb  { page-break-inside: avoid; break-inside: avoid; }
        .ndoc-pb  { page-break-before: always; break-before: page; }

        img, * {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          color-adjust: exact;
        }

        table { border-collapse: collapse; }

        .doc-meal-divider {
          border: none;
          border-top: 0.5pt solid #e8c9a8;
          margin: 6pt 0;
        }
      }
    `}</style>
  );
}

// ─── Cabeçalho ────────────────────────────────────────────────────────────────
function DocHeader({ nutri, patient, subtitle }) {
  const marca = useMarca();
  const nutriName = nutri?.name_display || nutri?.name || 'Nutricionista';
  const nutriCrn  = nutri?.crn || '';
  return (
    <div style={{ marginBottom: '16pt' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        paddingBottom: '10pt', borderBottom: `2pt solid ${GOLD}` }}>

        {/* Esquerda: logo + info nutricionista */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '11pt' }}>
          <img src={marca.logo || LOGO_LEVNUTRI} alt={marca.businessName || 'LevNutri'}
            style={{ ...reportLogoStyle(marca, 44), width: 'auto', objectFit: 'contain', flexShrink: 0 }} />
          <div style={{ borderLeft: `1.5pt solid ${CREAM2}`, paddingLeft: '10pt' }}>
            <p style={{ margin: 0, fontFamily: "'Nunito', sans-serif", fontSize: '12pt',
              fontWeight: 600, color: TEXT, letterSpacing: '0.01em' }}>
              {nutriName}
            </p>
            <p style={{ margin: '2pt 0 0', fontSize: '7.5pt', color: TEXT_LT,
              fontFamily: UI_FONT, letterSpacing: '0.04em' }}>
              {nutriCrn ? `${nutriCrn} · ` : ''}Nutricionista
            </p>
          </div>
        </div>

        {/* Direita: nome do paciente + data */}
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: 0, fontFamily: "'Nunito', sans-serif", fontSize: '19pt',
            fontWeight: 600, color: TEXT, lineHeight: 1.1 }}>
            {patient.name}
          </p>
          <p style={{ margin: '3pt 0 0', fontSize: '10pt', color: TEXT_LT, fontFamily: UI_FONT }}>
            {emo(subtitle)} · {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Rodapé ────────────────────────────────────────────────────────────────────
function DocFooter({ page, total }) {
  const marca = useMarca();
  return (
    <div style={{ borderTop: `0.6pt solid #d9d0c6`, paddingTop: '5pt', marginTop: '16pt',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <p style={{ margin: 0, fontSize: '6.5pt', color: '#a39b93', fontFamily: UI_FONT }}>
        {marca.businessName ? `${marca.businessName} · ` : ''}Documento gerado com LevNutri
      </p>
      {page && (
        <p style={{ margin: 0, fontSize: '6.5pt', color: '#a39b93', fontFamily: UI_FONT }}>
          {page}{total ? ` / ${total}` : ''}
        </p>
      )}
    </div>
  );
}

// ─── Seção título ──────────────────────────────────────────────────────────────
function SectionTitle({ icon, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6pt', marginBottom: '8pt',
      paddingBottom: '4pt', borderBottom: `1pt solid ${CREAM2}` }}>
      <span style={{ fontSize: '9pt' }}>{emo(icon)}</span>
      <p style={{ margin: 0, fontFamily: "'Nunito', sans-serif", fontSize: '10pt',
        fontWeight: 600, color: GOLD_DK }}>
        {emo(text)}
      </p>
    </div>
  );
}

// ─── PÁGINA 1: Plano Alimentar ─────────────────────────────────────────────────
function PaginaPlanoAlimentar({ nutri, patient, dietPlan, sections, pageNum, pageTotal }) {
  const meals = mealList(dietPlan.meals_plan);
  const guidelineLines = dietPlan.guidelines
    ? normText(dietPlan.guidelines).split('\n').map(l => l.trim()).filter(Boolean)
    : [];

  return (
    <div style={{ fontFamily: DOC_FONT, color: TEXT, fontSize: '8.5pt', lineHeight: 1.55, background: '#ffffff' }}>
      <DocHeader nutri={nutri} patient={patient} subtitle="Plano Alimentar" />

      <div style={{ textAlign: 'center', marginBottom: '14pt' }}>
        <h1 style={{ fontFamily: "'Nunito', sans-serif", fontSize: '17pt', fontWeight: 600,
          color: TEXT, margin: '0 0 3pt', letterSpacing: '-0.01em' }}>
          Plano Alimentar
        </h1>
        {dietPlan.plan_name && (
          <p style={{ margin: 0, fontSize: '9pt', color: TEXT_LT, fontFamily: UI_FONT, fontStyle: 'italic' }}>
            {dietPlan.plan_name}
          </p>
        )}
      </div>

      {/* Macros */}
      {sections.refeicoes && (
        <div className="ndoc-nb" style={{ display: 'grid',
          gridTemplateColumns: sections.agua ? 'repeat(5,1fr)' : 'repeat(4,1fr)',
          gap: '5pt', marginBottom: '14pt' }}>
          {[
            { label: 'Calorias/dia',  value: `${dietPlan.daily_calories} kcal`, highlight: true },
            { label: 'Proteínas',     value: `${fmtNum(dietPlan.protein_grams)} g` },
            { label: 'Carboidratos',  value: `${fmtNum(dietPlan.carbs_grams)} g` },
            { label: 'Gorduras',      value: `${fmtNum(dietPlan.fat_grams)} g` },
            ...(sections.agua ? [{ label: 'Água', value: `${dietPlan.water_recommendation} L/dia` }] : []),
          ].map(({ label, value, highlight }) => (
            <div key={label} style={{ textAlign: 'center', padding: '7pt 4pt',
              background: highlight ? CREAM : '#fbf8f5',
              border: `1pt solid ${highlight ? GOLD : '#d9d0c6'}`,
              borderBottom: `2.5pt solid ${highlight ? GOLD : '#d9d0c6'}`,
              borderRadius: '4pt' }}>
              <p style={{ margin: '0 0 2pt', fontSize: '6.5pt', color: TEXT_LT,
                fontFamily: UI_FONT, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {emo(label)}
              </p>
              <p style={{ margin: 0, fontSize: highlight ? '11pt' : '9.5pt', fontWeight: 600,
                color: highlight ? GOLD_DK : TEXT, fontFamily: "'Nunito', sans-serif" }}>
                {value}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Refeições */}
      {sections.refeicoes && meals.length > 0 && (
        <div style={{ marginBottom: '12pt' }}>
          <SectionTitle icon="🍽️" text="Refeições do Dia" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {meals.map((meal, i) => (
              <div key={i} className="ndoc-nb">
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8pt',
                  padding: '7pt 0 4pt', borderBottom: i < meals.length - 1 ? `0.5pt solid ${CREAM2}` : 'none' }}>
                  <div style={{ width: '6pt', height: '6pt', borderRadius: '50%',
                    background: GOLD, flexShrink: 0, marginTop: '2pt' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3pt' }}>
                      <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: '9.5pt', fontWeight: 600, color: TEXT }}>
                        {emo(meal.nome || MEAL_LABELS[meal._key])}
                      </span>
                      <span style={{ fontSize: '7pt', color: TEXT_LT, fontFamily: UI_FONT }}>
                        {[meal.horario, meal.calorias > 0 ? `${meal.calorias} kcal` : null].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '11pt', fontSize: '8pt', color: TEXT_LT,
                      lineHeight: 1.65, fontFamily: UI_FONT }}>
                      {(meal.alimentos || []).map((a, j) => (
                        <li key={j}>
                          <span style={{ color: TEXT }}>{a.alimento}</span>
                          {a.porcao && <span style={{ color: '#736b64', fontStyle: 'italic' }}> — {a.porcao}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suplementação do plano */}
      {sections.suplementacao && dietPlan.supplements?.length > 0 && (
        <div className="ndoc-nb" style={{ marginBottom: '12pt' }}>
          <SectionTitle icon="💊" text="Suplementação Prescrita" />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '7.5pt', fontFamily: UI_FONT }}>
            <thead>
              <tr style={{ background: CREAM }}>
                {['Suplemento', 'Dose', 'Momento de uso'].map(h => (
                  <th key={h} style={{ padding: '4pt 6pt', textAlign: 'left', fontWeight: 600,
                    color: TEXT_LT, borderBottom: `1pt solid ${CREAM2}`,
                    textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '6.5pt' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dietPlan.supplements.map((s, i) => (
                <tr key={i} style={{ borderBottom: `0.5pt solid ${CREAM2}` }}>
                  <td style={{ padding: '4pt 6pt', fontWeight: 600, color: TEXT }}>{emo(s.nome)}</td>
                  <td style={{ padding: '4pt 6pt', color: TEXT_LT }}>{s.dosagem || '—'}</td>
                  <td style={{ padding: '4pt 6pt', color: TEXT_LT }}>{s.momento_uso || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Orientações */}
      {sections.orientacoes && guidelineLines.length > 0 && (
        <div className="ndoc-nb" style={{ marginBottom: '12pt', padding: '10pt 14pt',
          background: CREAM, border: `1pt solid ${CREAM2}`, borderLeft: `3pt solid ${GOLD}`,
          borderRadius: '4pt' }}>
          <SectionTitle icon="✨" text="Orientações Personalizadas" />
          <ul style={{ margin: 0, paddingLeft: '12pt', fontSize: '8pt', color: TEXT_LT,
            lineHeight: 1.75, fontFamily: UI_FONT }}>
            {guidelineLines.map((line, i) => (
              <li key={i} style={{ marginBottom: '2pt' }}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      <DocFooter page={`Página ${pageNum}`} total={String(pageTotal)} />
    </div>
  );
}

// ─── PÁGINA 2: Recomendações ───────────────────────────────────────────────────
function PaginaRecomendacoes({ nutri, patient, recommendations, sections, pageNum, pageTotal, proximaConsulta, addPageBreak }) {
  const rec = recommendations || {};
  const hasStr = (v) => typeof v === 'string' && v.trim().length > 0;
  const hasArr = (v) => Array.isArray(v) && v.length > 0;

  const nutriName = nutri?.name_display || nutri?.name || 'Nutricionista';
  const nutriCrn  = nutri?.crn || '';

  const hasAnyRec = (sections.agua && hasStr(rec.water_intake))
    || (sections.alimentos && hasArr(rec.foods_to_avoid))
    || (sections.suplementos && hasArr(rec.supplements))
    || (sections.atividade && hasArr(rec.physical_activity))
    || (sections.outras && hasStr(rec.other_notes))
    || (sections.proxima && proximaConsulta);

  return (
    <div className={addPageBreak ? 'ndoc-pb' : ''} style={{ fontFamily: DOC_FONT, color: TEXT, fontSize: '8.5pt',
      lineHeight: 1.55, background: '#ffffff' }}>
      <DocHeader nutri={nutri} patient={patient} subtitle="Recomendações" />

      <div style={{ textAlign: 'center', marginBottom: '14pt' }}>
        <h1 style={{ fontFamily: "'Nunito', sans-serif", fontSize: '17pt', fontWeight: 600,
          color: TEXT, margin: '0 0 3pt' }}>
          Recomendações Nutricionais
        </h1>
        <p style={{ margin: 0, fontSize: '8.5pt', color: TEXT_LT, fontFamily: UI_FONT }}>
          Orientações personalizadas para {emo(patient.name.split(' ')[0])}
        </p>
      </div>

      {!hasAnyRec && (
        <div style={{ padding: '24pt', textAlign: 'center', background: CREAM,
          border: `1pt solid ${CREAM2}`, borderRadius: '6pt' }}>
          <p style={{ margin: 0, fontSize: '9pt', color: TEXT_LT, fontStyle: 'italic', fontFamily: UI_FONT }}>
            Nenhuma recomendação selecionada.
          </p>
        </div>
      )}

      {/* Água */}
      {sections.agua && hasStr(rec.water_intake) && (
        <div className="ndoc-nb" style={{ marginBottom: '11pt', padding: '9pt 13pt',
          background: '#edf1f6', border: `1pt solid #edf1f6`, borderLeft: `3pt solid #7189a6`,
          borderRadius: '4pt' }}>
          <SectionTitle icon="💧" text="Ingestão de Água" />
          <p style={{ margin: 0, fontSize: '9pt', color: TEXT, fontFamily: UI_FONT, fontWeight: 500 }}>
            {rec.water_intake}
          </p>
        </div>
      )}

      {/* Alimentos a evitar */}
      {sections.alimentos && hasArr(rec.foods_to_avoid) && (
        <div className="ndoc-nb" style={{ marginBottom: '11pt', padding: '9pt 13pt',
          background: '#f8ebe9', border: `1pt solid #f8ebe9`, borderLeft: `3pt solid #c46a60`,
          borderRadius: '4pt' }}>
          <SectionTitle icon="🚫" text="Alimentos a Evitar" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4pt' }}>
            {rec.foods_to_avoid.map((food, i) => (
              <span key={i} style={{ display: 'inline-block', padding: '2pt 7pt',
                background: '#ffffff', border: `0.8pt solid #e8c9a8`,
                borderRadius: '10pt', fontSize: '7.5pt', color: '#7a2e27',
                fontFamily: UI_FONT, fontWeight: 500 }}>
                {food}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Suplementos */}
      {sections.suplementos && hasArr(rec.supplements) && (
        <div className="ndoc-nb" style={{ marginBottom: '11pt' }}>
          <SectionTitle icon="💊" text="Suplementos e Fitoterápicos" />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '7.5pt', fontFamily: UI_FONT }}>
            <thead>
              <tr style={{ background: CREAM }}>
                {['Suplemento', 'Dose', 'Frequência', 'Observação'].map(h => (
                  <th key={h} style={{ padding: '4pt 6pt', textAlign: 'left', fontWeight: 600,
                    color: TEXT_LT, borderBottom: `1pt solid ${CREAM2}`,
                    textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '6.5pt' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rec.supplements.map((s, i) => (
                <tr key={i} style={{ borderBottom: `0.5pt solid ${CREAM2}`,
                  background: i % 2 === 0 ? '#ffffff' : '#fbf8f5' }}>
                  <td style={{ padding: '4pt 6pt', fontWeight: 600, color: TEXT }}>{emo(s.nome)}</td>
                  <td style={{ padding: '4pt 6pt', color: TEXT_LT }}>{s.dose || '—'}</td>
                  <td style={{ padding: '4pt 6pt', color: TEXT_LT }}>{s.frequencia || '—'}</td>
                  <td style={{ padding: '4pt 6pt', color: TEXT_LT, fontStyle: s.observacao ? 'italic' : 'normal' }}>
                    {s.observacao || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Atividade Física */}
      {sections.atividade && hasArr(rec.physical_activity) && (
        <div className="ndoc-nb" style={{ marginBottom: '11pt', padding: '9pt 13pt',
          background: '#edf4ee', border: `1pt solid #b9d2bd`, borderLeft: `3pt solid #46714f`,
          borderRadius: '4pt' }}>
          <SectionTitle icon="🏃" text="Atividade Física" />
          <ul style={{ margin: 0, paddingLeft: '11pt', fontSize: '8pt', color: TEXT_LT,
            lineHeight: 1.75, fontFamily: UI_FONT }}>
            {rec.physical_activity.map((ex, i) => (
              <li key={i} style={{ marginBottom: '3pt' }}>
                <span style={{ fontWeight: 600, color: TEXT }}>{ex.tipo}</span>
                {ex.frequencia && <span> · {ex.frequencia}×/semana</span>}
                {ex.duracao    && <span> · {ex.duracao} min</span>}
                {ex.intensidade && <span> · intensidade {ex.intensidade}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Outras recomendações */}
      {sections.outras && hasStr(rec.other_notes) && (
        <div className="ndoc-nb" style={{ marginBottom: '11pt', padding: '9pt 13pt',
          background: CREAM, border: `1pt solid ${CREAM2}`, borderLeft: `3pt solid ${GOLD}`,
          borderRadius: '4pt' }}>
          <SectionTitle icon="📝" text="Outras Recomendações" />
          <p style={{ margin: 0, fontSize: '8.5pt', color: TEXT_LT, fontFamily: UI_FONT,
            lineHeight: 1.75, fontStyle: 'italic', whiteSpace: 'pre-line' }}>
            {normText(rec.other_notes)}
          </p>
        </div>
      )}

      {/* Próxima consulta */}
      {sections.proxima && proximaConsulta && (
        <div className="ndoc-nb" style={{ marginBottom: '11pt', padding: '9pt 13pt',
          background: '#edf1f6', border: `1pt solid #b8c7d8`, borderLeft: `3pt solid #7189a6`,
          borderRadius: '4pt' }}>
          <SectionTitle icon="📅" text="Próxima Consulta" />
          <p style={{ margin: 0, fontSize: '9pt', color: TEXT, fontFamily: UI_FONT, fontWeight: 500 }}>
            {fmtDateTime(proximaConsulta.data_hora)}
          </p>
          <p style={{ margin: '3pt 0 0', fontSize: '8pt', color: TEXT_LT, fontFamily: UI_FONT }}>
            {emo(proximaConsulta.tipo === 'online' ? '💻 Online' : '🏥 Presencial')}
            {proximaConsulta.duracao_minutos && ` · ${proximaConsulta.duracao_minutos} min`}
          </p>
        </div>
      )}

      {/* Assinatura */}
      <div className="ndoc-nb" style={{ marginTop: '14pt', padding: '12pt 16pt',
        background: CREAM, border: `1pt solid ${CREAM2}`, borderRadius: '6pt', textAlign: 'center' }}>
        <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '9.5pt',
          fontStyle: 'italic', color: GOLD_DK, margin: '0 0 10pt', lineHeight: 1.75 }}>
          "Cada escolha que você faz hoje é um investimento na sua saúde e bem-estar."
        </p>
        <div style={{ borderTop: `0.8pt solid ${CREAM2}`, paddingTop: '9pt' }}>
          <p style={{ margin: '0 0 1pt', fontFamily: "'Nunito', sans-serif", fontSize: '9pt',
            fontWeight: 600, color: TEXT }}>
            {nutriName}
          </p>
          <p style={{ margin: 0, fontSize: '7.5pt', color: TEXT_LT, fontFamily: UI_FONT }}>
            {nutriCrn} · Nutricionista
          </p>
        </div>
      </div>

      <DocFooter page={`Página ${pageNum}`} total={String(pageTotal)} />
    </div>
  );
}

// ─── Página: Evolução do Tratamento ───────────────────────────────────────────
function PaginaEvolucao({ nutri, patient, evolution, pageNum, pageTotal, addPageBreak }) {
  const nutriName = nutri?.name || '';
  const nutriCrn  = nutri?.crn  ? `CRN ${nutri.crn}` : '';

  // Medições ordenadas: mais antiga primeiro
  const sorted = [...evolution].sort((a, b) => a.data_medicao.localeCompare(b.data_medicao));
  const first  = sorted[0];
  const last   = sorted[sorted.length - 1];

  const diffPeso  = last && first ? (parseFloat(last.peso) - parseFloat(first.peso)) : 0;
  const diffMusc  = last && first ? (parseFloat(last.musculos_esqueleticos) - parseFloat(first.musculos_esqueleticos)) : 0;
  const diffGord  = last && first ? (parseFloat(last.gordura_corporal) - parseFloat(first.gordura_corporal)) : 0;

  let motivText = 'Continue firme — cada pequeno passo conta na sua jornada de saúde!';
  if (Math.abs(diffPeso) >= 0.5) {
    if (diffPeso < 0) motivText = `Você já eliminou ${Math.abs(diffPeso).toFixed(1)} kg desde o início! Continue assim. 💪`;
    else              motivText = `Você ganhou ${diffPeso.toFixed(1)} kg desde o início — progresso na composição corporal! 💪`;
  }
  if (diffMusc >= 0.5) motivText = `Massa muscular aumentou ${diffMusc.toFixed(1)} kg — seu corpo está ficando mais forte! 💪`;
  if (Math.abs(diffPeso) < 0.3 && Math.abs(diffGord) < 1) motivText = 'Você está mantendo excelentes resultados! Consistência é a chave. ✨';

  const fmtDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : '-';
  const fmtN = (v, u = '') => v != null && v !== '' ? `${parseFloat(v).toFixed(1)}${u}` : '-';

  return (
    <div style={{
      fontFamily: DOC_FONT, color: TEXT, fontSize: '8.5pt',
      pageBreakBefore: addPageBreak ? 'always' : 'auto',
    }}>
      <DocHeader nutri={nutri} patient={patient} subtitle="Evolução do Tratamento" />

      <h2 style={{ fontFamily: "'Nunito', sans-serif", fontSize: '12pt', fontWeight: 600, color: GOLD_DK, margin: '0 0 10pt', borderBottom: `1pt solid ${CREAM2}`, paddingBottom: '6pt' }}>
        <Ico e="📈" sp />Evolução do Tratamento
      </h2>

      {/* Tabela de medições */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '14pt', fontSize: '7.8pt' }}>
        <thead>
          <tr style={{ background: CREAM2 }}>
            {['Data', 'Peso (kg)', 'IMC', '% Gordura', 'Massa Musc. (%)'].map(h => (
              <th key={h} style={{ padding: '5pt 7pt', textAlign: 'center', fontWeight: 600, color: GOLD_DK, borderBottom: `1.5pt solid ${CREAM2}`, fontFamily: UI_FONT, fontSize: '7pt', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={r.id || i} style={{ background: i % 2 === 0 ? '#ffffff' : CREAM }}>
              <td style={{ padding: '4pt 7pt', textAlign: 'center', borderBottom: `0.5pt solid ${CREAM2}` }}>{fmtDate(r.data_medicao)}</td>
              <td style={{ padding: '4pt 7pt', textAlign: 'center', borderBottom: `0.5pt solid ${CREAM2}` }}>{fmtN(r.peso)}</td>
              <td style={{ padding: '4pt 7pt', textAlign: 'center', borderBottom: `0.5pt solid ${CREAM2}` }}>{fmtN(r.imc)}</td>
              <td style={{ padding: '4pt 7pt', textAlign: 'center', borderBottom: `0.5pt solid ${CREAM2}` }}>{fmtN(r.gordura_corporal, '%')}</td>
              <td style={{ padding: '4pt 7pt', textAlign: 'center', borderBottom: `0.5pt solid ${CREAM2}` }}>{fmtN(r.musculos_esqueleticos, '%')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mensagem motivacional */}
      <div style={{ background: CREAM, border: `1pt solid ${CREAM2}`, borderRadius: '6pt', padding: '10pt 14pt', marginBottom: '12pt' }}>
        <p style={{ margin: 0, fontSize: '9pt', fontStyle: 'italic', color: GOLD_DK, fontFamily: UI_FONT, lineHeight: 1.5 }}>
          "{emo(motivText)}"
        </p>
      </div>

      <div style={{ height: '1pt', background: CREAM2, margin: '10pt 0' }} />

      {/* Assinatura */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '9pt' }}>
        <div>
          <p style={{ margin: '0 0 1pt', fontFamily: "'Nunito', sans-serif", fontSize: '9pt', fontWeight: 600, color: TEXT }}>{nutriName}</p>
          <p style={{ margin: 0, fontSize: '7.5pt', color: TEXT_LT, fontFamily: UI_FONT }}>{nutriCrn} · Nutricionista</p>
        </div>
      </div>

      <DocFooter page={`Página ${pageNum}`} total={String(pageTotal)} />
    </div>
  );
}

// ─── Página: Composição Corporal (bioimpedância + antropometria na figura) ────
const DOBRAS = [
  ['dc_peitoral', 'Peitoral'], ['dc_axilar_media', 'Axilar média'], ['dc_triceps', 'Tríceps'],
  ['dc_subescapular', 'Subescapular'], ['dc_abdominal', 'Abdominal'],
  ['dc_suprailiaca', 'Suprailíaca'], ['dc_coxa', 'Coxa'],
];
const temValor = (v) => v != null && v !== '' && !isNaN(parseFloat(v));
const porData = (lista) => [...(lista || [])].sort((a, b) => String(a.data_medicao).localeCompare(String(b.data_medicao)));
const dataBR = (d) => d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR') : '';

function classifyRCQ(v, gender) {
  const r = parseFloat(v);
  if (isNaN(r)) return null;
  const [ok, alto] = gender === 'M' ? [0.95, 1.0] : [0.80, 0.85];
  if (r < ok)    return { label: 'Adequado', color: '#46714f' };
  if (r <= alto) return { label: 'Atenção',  color: '#c4933f' };
  return                { label: 'Alto',     color: '#a8483f' };
}

function idadeAnos(nasc) {
  if (!nasc) return null;
  const b = new Date(String(nasc).slice(0, 10) + 'T12:00:00');
  if (isNaN(b)) return null;
  const n = new Date();
  let a = n.getFullYear() - b.getFullYear();
  if (n.getMonth() < b.getMonth() || (n.getMonth() === b.getMonth() && n.getDate() < b.getDate())) a--;
  return a;
}

function MetricaBio({ m }) {
  const { label, valor, un, casas = 1, classe, diff, cair } = m;
  const corDiff = diff == null || Math.abs(diff) < 0.05 || cair == null ? TEXT_LT
    : ((cair ? diff < 0 : diff > 0) ? '#46714f' : '#a8483f');
  return (
    <div style={{ border: `0.8pt solid ${CREAM2}`, borderRadius: '6pt', padding: '5pt 7pt', marginBottom: '6pt', background: '#ffffff' }}>
      <p style={{ margin: 0, fontSize: '6.5pt', color: TEXT_LT, fontFamily: UI_FONT, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</p>
      <p style={{ margin: '1pt 0 0', fontSize: '12pt', fontWeight: 700, color: TEXT, fontFamily: UI_FONT }}>
        {fmtBR(valor, casas)}<span style={{ fontSize: '7.5pt', fontWeight: 600, color: TEXT_LT }}>{un}</span>
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4pt', flexWrap: 'wrap', marginTop: '2pt' }}>
        {classe && (
          <span style={{ fontSize: '6.3pt', fontWeight: 700, color: classe.color, background: `${classe.color}1f`,
            borderRadius: '8pt', padding: '1pt 5pt', fontFamily: UI_FONT }}>{classe.label}</span>
        )}
        {diff != null && Math.abs(diff) >= 0.05 && (
          <span style={{ fontSize: '6.8pt', fontWeight: 700, color: corDiff, fontFamily: UI_FONT }}>
            {diff > 0 ? '+' : '−'}{fmtBR(Math.abs(diff), casas)}{un.trim() === '%' ? ' p.p.' : un}
          </span>
        )}
      </div>
    </div>
  );
}

function PaginaComposicao({ nutri, patient, bio, antro, mostrarBio, mostrarMedidas, pageNum, pageTotal, addPageBreak }) {
  const gender = patient?.gender === 'M' ? 'M' : 'F';
  const bioOrd = mostrarBio ? porData(bio) : [];
  const b0 = bioOrd[0], b1 = bioOrd[bioOrd.length - 1];
  const antOrd = mostrarMedidas ? porData(antro) : [];
  const a0 = antOrd[0], a1 = antOrd[antOrd.length - 1];
  const temBio = !!b1, temAntro = !!a1;
  const medidas = temAntro ? medidasDoCorpo(a1, a0, gender) : [];

  const dif = (k) => (b1 && b0 && b0 !== b1 && temValor(b1[k]) && temValor(b0[k])) ? parseFloat(b1[k]) - parseFloat(b0[k]) : null;
  const met = (label, k, un, classe, cair, casas = 1) =>
    (b1 && temValor(b1[k])) ? { label, valor: parseFloat(b1[k]), un, classe, cair, casas, diff: dif(k) } : null;
  const esquerda = temBio ? [
    met('Peso', 'peso', ' kg', null, true),
    met('IMC', 'imc', '', classifyIMC(b1.imc), true),
    met('% Gordura', 'gordura_corporal', '%', classifyGordura(b1.gordura_corporal, gender), true),
    met('Gordura visceral', 'gordura_visceral', ' nív.', classifyVisceral(b1.gordura_visceral), true, 0),
  ].filter(Boolean) : [];
  const direita = temBio ? [
    met('% Músculo', 'musculos_esqueleticos', '%', classifyMuscle(b1.musculos_esqueleticos, gender), false),
    met('Metabolismo basal', 'metabolismo_basal', ' kcal', null, null, 0),
    met('Idade corporal', 'idade_corporal', ' anos', classifyCorpAge(b1.idade_corporal, idadeAnos(patient?.birth_date)), true, 0),
  ].filter(Boolean) : [];

  const rcq = temAntro ? (temValor(a1.rcq) ? parseFloat(a1.rcq)
    : (temValor(a1.circ_cintura) && temValor(a1.circ_quadril) ? parseFloat(a1.circ_cintura) / parseFloat(a1.circ_quadril) : null)) : null;
  const resultados = temAntro ? [
    rcq != null && { label: 'Relação cintura-quadril', valor: fmtBR(rcq, 2), classe: classifyRCQ(rcq, gender) },
    temValor(a1.percentual_gordura) && { label: '% Gordura (dobras)', valor: `${fmtBR(a1.percentual_gordura)}%`, classe: classifyGordura(a1.percentual_gordura, gender) },
    temValor(a1.massa_magra) && { label: 'Massa magra', valor: `${fmtBR(a1.massa_magra)} kg` },
    temValor(a1.massa_gorda) && { label: 'Massa gorda', valor: `${fmtBR(a1.massa_gorda)} kg` },
    temValor(a1.soma_dobras) && { label: 'Soma das dobras', valor: `${fmtBR(a1.soma_dobras)} mm` },
  ].filter(Boolean) : [];
  const dobras = temAntro ? DOBRAS.filter(([k]) => temValor(a1[k])) : [];

  const linhaData = [
    temBio && `Bioimpedância de ${dataBR(b1.data_medicao)}${b0 !== b1 ? ` (comparada a ${dataBR(b0.data_medicao)})` : ''}`,
    temAntro && `Antropometria de ${dataBR(a1.data_medicao)}${a0 !== a1 ? ` (comparada a ${dataBR(a0.data_medicao)})` : ''}`,
  ].filter(Boolean).join(' · ');

  const larguraFigura = temBio ? 300 : 380;
  const coluna = { width: '100pt', flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' };

  return (
    <div style={{ fontFamily: DOC_FONT, color: TEXT, fontSize: '8.5pt', pageBreakBefore: addPageBreak ? 'always' : 'auto' }}>
      <DocHeader nutri={nutri} patient={patient} subtitle="Composição Corporal" />

      <h2 style={{ fontFamily: UI_FONT, fontSize: '12pt', fontWeight: 600, color: GOLD_DK, margin: '0 0 3pt',
        borderBottom: `1pt solid ${CREAM2}`, paddingBottom: '4pt' }}>
        Composição Corporal
      </h2>
      {linhaData && <p style={{ margin: '0 0 8pt', fontSize: '7pt', color: TEXT_LT, fontFamily: UI_FONT }}>{linhaData}</p>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6pt', marginBottom: '10pt' }}>
        {temBio && <div style={coluna}>{esquerda.map(m => <MetricaBio key={m.label} m={m} />)}</div>}
        <CorpoAnotado gender={gender} medidas={medidas} largura={larguraFigura} altura={330} />
        {temBio && <div style={coluna}>{direita.map(m => <MetricaBio key={m.label} m={m} />)}</div>}
      </div>

      {resultados.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${resultados.length}, 1fr)`, gap: '6pt', marginBottom: '8pt' }}>
          {resultados.map(r => (
            <div key={r.label} style={{ background: CREAM, border: `0.8pt solid ${CREAM2}`, borderRadius: '6pt', padding: '5pt 7pt' }}>
              <p style={{ margin: 0, fontSize: '6.3pt', color: TEXT_LT, fontFamily: UI_FONT, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{r.label}</p>
              <p style={{ margin: '1pt 0 0', fontSize: '11pt', fontWeight: 700, color: TEXT, fontFamily: UI_FONT }}>{r.valor}</p>
              {r.classe && (
                <span style={{ fontSize: '6.3pt', fontWeight: 700, color: r.classe.color, background: `${r.classe.color}1f`,
                  borderRadius: '8pt', padding: '1pt 5pt', fontFamily: UI_FONT }}>{r.classe.label}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {dobras.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8pt', fontSize: '7.5pt', fontFamily: UI_FONT }}>
          <thead>
            <tr style={{ background: CREAM2 }}>
              <th style={{ padding: '3pt 5pt', textAlign: 'left', color: GOLD_DK, fontWeight: 600 }}>Dobras cutâneas (mm)</th>
              {dobras.map(([k, l]) => <th key={k} style={{ padding: '3pt 5pt', textAlign: 'center', color: GOLD_DK, fontWeight: 600 }}>{l}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '3pt 5pt', color: TEXT_LT, borderBottom: `0.5pt solid ${CREAM2}` }}>{dataBR(a1.data_medicao)}</td>
              {dobras.map(([k]) => <td key={k} style={{ padding: '3pt 5pt', textAlign: 'center', fontWeight: 600, borderBottom: `0.5pt solid ${CREAM2}` }}>{fmtBR(a1[k])}</td>)}
            </tr>
          </tbody>
        </table>
      )}

      <p style={{ margin: '0 0 6pt', fontSize: '6.5pt', color: TEXT_LT, fontFamily: UI_FONT, fontStyle: 'italic' }}>
        Ilustração meramente indicativa dos pontos de medida. Classificações de referência; a interpretação é feita pela nutricionista.
      </p>

      <DocFooter page={`Página ${pageNum}`} total={String(pageTotal)} />
    </div>
  );
}

// ─── Conteúdo do documento (respeita seções selecionadas) ─────────────────────
function DocumentoContent({ reportData, recommendations, sections, proximaConsulta, evolution, antropometria }) {
  const { patient, nutri, diet_plan } = reportData;

  const planoSecs  = ['refeicoes', 'suplementacao', 'orientacoes', 'agua'];
  const recSecs    = ['agua', 'alimentos', 'suplementos', 'atividade', 'outras', 'proxima'];
  const hasPlano   = diet_plan && planoSecs.some(k => sections[k]);
  const hasRec     = recSecs.some(k => sections[k]);
  const hasEvolucao = sections.evolucao && evolution?.length > 0;
  const mostrarBio     = !!sections.composicao && evolution?.length > 0;
  const mostrarMedidas = !!sections.medidas && antropometria?.length > 0;
  const hasCorpo    = mostrarBio || mostrarMedidas;

  let pageCount = 0;
  if (hasPlano)    pageCount++;
  if (hasRec)      pageCount++;
  if (hasEvolucao) pageCount++;
  if (hasCorpo)    pageCount++;
  if (pageCount === 0) pageCount = 1;
  const pageTotal = pageCount;

  let pageNum = 0;

  return (
    <div>
      {hasPlano ? (
        <PaginaPlanoAlimentar
          nutri={nutri} patient={patient} dietPlan={diet_plan}
          sections={sections} pageNum={++pageNum} pageTotal={pageTotal}
        />
      ) : !hasRec && !hasEvolucao && !hasCorpo ? (
        <div style={{ fontFamily: DOC_FONT, color: TEXT, fontSize: '8.5pt' }}>
          <DocHeader nutri={nutri} patient={patient} subtitle="Documento do Paciente" />
          <div style={{ padding: '24pt', textAlign: 'center', background: CREAM,
            border: `1pt solid ${CREAM2}`, borderRadius: '6pt', marginBottom: '12pt' }}>
            <p style={{ margin: 0, fontSize: '9pt', color: TEXT_LT, fontStyle: 'italic', fontFamily: UI_FONT }}>
              Nenhuma seção selecionada.
            </p>
          </div>
          <DocFooter page="Página 1" total="1" />
        </div>
      ) : null}

      {hasRec && (
        <PaginaRecomendacoes
          nutri={nutri} patient={patient} recommendations={recommendations}
          sections={sections} pageNum={++pageNum} pageTotal={pageTotal}
          proximaConsulta={proximaConsulta} addPageBreak={hasPlano}
        />
      )}

      {hasEvolucao && (
        <PaginaEvolucao
          nutri={nutri} patient={patient} evolution={evolution}
          pageNum={++pageNum} pageTotal={pageTotal} addPageBreak={hasPlano || hasRec}
        />
      )}

      {hasCorpo && (
        <PaginaComposicao
          nutri={nutri} patient={patient} bio={evolution} antro={antropometria}
          mostrarBio={mostrarBio} mostrarMedidas={mostrarMedidas}
          pageNum={++pageNum} pageTotal={pageTotal} addPageBreak={hasPlano || hasRec || hasEvolucao}
        />
      )}
    </div>
  );
}

// ─── Portal de impressão ───────────────────────────────────────────────────────
function PrintPortal({ reportData, recommendations, sections, proximaConsulta, evolution, antropometria }) {
  return ReactDOM.createPortal(
    <div className="lev-relatorio-print">
      <DocumentoContent
        reportData={reportData} recommendations={recommendations}
        sections={sections} proximaConsulta={proximaConsulta}
        evolution={evolution} antropometria={antropometria}
      />
    </div>,
    document.body
  );
}

// ─── Tela de seleção de seções ─────────────────────────────────────────────────
function SelectionScreen({ sections, onChange, onGerarTudo, onGerar, reportData, recommendations, disponivel = {} }) {
  const [hovered, setHovered] = useState(null);

  const grupos = [
    { id: 'plano', label: '📄 Plano Alimentar', color: GOLD },
    { id: 'rec',   label: '🌿 Recomendações',   color: '#46714f' },
    { id: 'extra', label: '⭐ Extras',           color: '#7189a6' },
    { id: 'corpo', label: '💪 Composição corporal', color: '#9a6a42' },
  ];

  const hasDietPlan = !!reportData?.diet_plan;
  const hasRec      = !!recommendations;

  const isDisabled = (sec) =>
    (sec.group === 'plano' && !hasDietPlan) || (sec.group === 'rec' && !hasRec)
    || (sec.key === 'composicao' && !disponivel.composicao) || (sec.key === 'medidas' && !disponivel.medidas);

  const toggle = (key) => onChange(prev => ({ ...prev, [key]: !prev[key] }));
  const markAll = (val) => {
    const next = {};
    SECTION_DEFS.forEach(s => { next[s.key] = val; });
    onChange(next);
  };

  const totalSelected = SECTION_DEFS.filter(s => sections[s.key] && !isDisabled(s)).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', fontFamily: UI_FONT }}>

      {/* Header */}
      <div style={{ padding: '1.1rem 1.5rem 0.9rem', borderBottom: '1px solid #f6f0ea' }}>
        <p style={{ margin: '0 0 0.15rem', fontSize: '0.95rem', fontWeight: 600, color: '#2f2a26' }}>
          Personalizar Relatório
        </p>
        <p style={{ margin: 0, fontSize: '0.78rem', color: '#736b64' }}>
          Escolha as seções a incluir. O PDF terá 1 ou 2 páginas conforme a seleção.
        </p>
      </div>

      {/* Lista de seções */}
      <div>
        {grupos.map((grupo, gi) => {
          const itens = SECTION_DEFS.filter(s => s.group === grupo.id);
          return (
            <div key={grupo.id}>
              {/* Cabeçalho do grupo */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                padding: '0.8rem 1.5rem 0.35rem',
                borderTop: gi > 0 ? '1px solid #f6f0ea' : 'none',
                marginTop: gi > 0 ? '0.2rem' : 0,
              }}>
                <span style={{
                  fontSize: '0.68rem', fontWeight: 700, color: grupo.color,
                  textTransform: 'uppercase', letterSpacing: '0.09em',
                }}>
                  {emo(grupo.label)}
                </span>
              </div>

              {/* Itens */}
              {itens.map(sec => {
                const disabled = isDisabled(sec);
                const checked  = sections[sec.key] && !disabled;
                const isHov    = hovered === sec.key && !disabled;

                return (
                  <label
                    key={sec.key}
                    title={disabled ? 'Sem dados cadastrados para esta paciente' : ''}
                    onMouseEnter={() => !disabled && setHovered(sec.key)}
                    onMouseLeave={() => setHovered(null)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.85rem',
                      padding: '0.45rem 1.5rem',
                      background: isHov ? CREAM : 'transparent',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.38 : 1,
                      transition: 'background .1s',
                      userSelect: 'none',
                    }}
                  >
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => !disabled && toggle(sec.key)}
                      style={{
                        width: 16, height: 16, flexShrink: 0,
                        accentColor: GOLD,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                      }}
                    />
                    {/* Nome */}
                    <span style={{
                      fontSize: '0.875rem', fontWeight: 600, color: '#2f2a26',
                      flexShrink: 0, width: 180,
                    }}>
                      {emo(sec.label)}
                      {disabled && (
                        <span style={{ fontSize: '0.7rem', fontWeight: 400, color: '#a39b93', marginLeft: '0.3rem' }}>
                          — sem dados
                        </span>
                      )}
                    </span>
                    {/* Descrição */}
                    <span style={{ fontSize: '0.78rem', color: '#736b64', lineHeight: 1.35 }}>
                      {emo(sec.desc)}
                    </span>
                  </label>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Rodapé sticky */}
      <div style={{
        position: 'sticky', bottom: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.8rem 1.5rem',
        background: '#ffffff',
        borderTop: '1px solid #f6f0ea',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.05)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() => markAll(true)}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: '0.78rem', color: GOLD_DK, fontWeight: 600,
              textDecoration: 'underline', fontFamily: UI_FONT,
            }}
          >
            Selecionar tudo
          </button>
          <span style={{ fontSize: '0.78rem', color: '#a39b93', fontFamily: UI_FONT }}>
            {totalSelected} {totalSelected === 1 ? 'seção selecionada' : 'seções selecionadas'}
          </span>
        </div>

        <button
          onClick={onGerar}
          disabled={totalSelected === 0}
          style={{
            padding: '0.58rem 1.4rem', background: GOLD, color: '#ffffff', border: 'none',
            borderRadius: '8px', fontWeight: 600, fontSize: '0.88rem', fontFamily: UI_FONT,
            cursor: totalSelected === 0 ? 'not-allowed' : 'pointer',
            opacity: totalSelected === 0 ? 0.5 : 1,
          }}
        >
          Gerar Relatório →
        </button>
      </div>
    </div>
  );
}

// ─── Modal principal ───────────────────────────────────────────────────────────
export default function DocumentoPacienteModal({ patientId, patientName, onClose }) {
  const [reportData,       setReportData]       = useState(null);
  const [recommendations,  setRecommendations]  = useState(null);
  const [proximaConsulta,  setProximaConsulta]  = useState(null);
  const [evolution,        setEvolution]        = useState(null);
  const [antropometria,    setAntropometria]    = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState('');
  const [printing,         setPrinting]         = useState(false);
  const [sections,         setSections]         = useState(DEFAULT_SECTIONS);
  const [configVisible,    setConfigVisible]    = useState(true);

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([
      reportsService.getData(patientId),
      recommendationsService.getByPatient(patientId),
    ]).then(([rptRes, recRes]) => {
      if (rptRes?.success) setReportData(rptRes.data);
      else setError('Erro ao carregar dados do paciente.');
      if (recRes?.success && recRes.data) setRecommendations(recRes.data);
    }).catch((err) => {
      console.error('[DocumentoPaciente] erro:', err);
      setError('Erro de comunicação com o servidor.');
    }).finally(() => setLoading(false));
  }, [patientId]);

  // Busca próxima consulta quando a seção é ativada
  const fetchProxima = useCallback(() => {
    appointmentsService.getNextByPatient(patientId)
      .then(res => { if (res?.success) setProximaConsulta(res.data || null); })
      .catch(() => {});
  }, [patientId]);

  useEffect(() => {
    if (sections.proxima) fetchProxima();
  }, [sections.proxima, fetchProxima]);

  // Pré-carrega as silhuetas: a impressão monta o documento logo antes do window.print().
  useEffect(() => { ['/silhueta-mulher.png', '/silhueta-homem.png'].forEach(src => { const i = new Image(); i.src = src; }); }, []);

  // Bioimpedância e antropometria: carregadas ao abrir, para saber o que existe
  // e já marcar "Composição corporal" / "Medidas" quando houver dados.
  useEffect(() => {
    let vivo = true;
    Promise.all([
      bioimpedanciaService.getByPatient(patientId, 20).catch(() => null),
      anthropometryService.getByPatient(patientId, 20).catch(() => null),
    ]).then(([bioRes, antRes]) => {
      if (!vivo) return;
      // A API pode devolver a lista direta ou { records: [...] }.
      const lista = (r) => !r?.success ? [] : (Array.isArray(r.data) ? r.data : (r.data?.records || []));
      const bio = lista(bioRes);
      const ant = lista(antRes);
      setEvolution(bio);
      setAntropometria(ant);
      setSections(prev => ({ ...prev, composicao: bio.length > 0, medidas: ant.length > 0 }));
    });
    return () => { vivo = false; };
  }, [patientId]);

  const handleGerarTudo = () => {
    const all = {};
    SECTION_DEFS.forEach(s => { all[s.key] = true; });
    all.composicao = (evolution?.length || 0) > 0;
    all.medidas = (antropometria?.length || 0) > 0;
    setSections(all);
    setConfigVisible(false);
  };

  const handleGerar = () => setConfigVisible(false);

  const handlePrint = () => {
    if (!reportData) return;
    const prev = document.title;
    const date = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    document.title = `PlanoAlimentar_${patientName}_${date}`;
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.title = prev;
        setPrinting(false);
      }, 700);
    }, 200);
  };

  return (
    <>
      <DocCss />
      {printing && reportData && (
        <PrintPortal
          reportData={reportData} recommendations={recommendations}
          sections={sections} proximaConsulta={proximaConsulta}
          evolution={evolution} antropometria={antropometria}
        />
      )}

      {/* Overlay */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 9100, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: '1rem', overflowY: 'auto' }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div style={{
          background: '#ffffff', borderRadius: '14px', width: '100%',
          maxWidth: configVisible ? '560px' : '820px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)', margin: 'auto', display: 'flex',
          flexDirection: 'column', overflow: 'hidden',
          transition: 'max-width .2s ease',
        }}>

          {/* Barra de ações */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '0.8rem 1.4rem', background: CREAM,
            borderBottom: `1px solid ${CREAM2}`, gap: '0.75rem', flexShrink: 0 }}>
            <div>
              <p style={{ margin: 0, fontWeight: 600, fontSize: '0.95rem', color: TEXT, fontFamily: UI_FONT }}>
                <Ico e="📄" sp />Documento do Paciente
              </p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: TEXT_LT, fontFamily: UI_FONT }}>
                {patientName} · {configVisible ? 'Selecione as seções' : 'Plano Alimentar + Recomendações'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
              {!configVisible && (
                <button
                  onClick={() => setConfigVisible(true)}
                  style={{ padding: '0.5rem 0.9rem', background: '#ffffff', color: GOLD_DK,
                    border: `1px solid ${GOLD}`, borderRadius: '8px', cursor: 'pointer',
                    fontWeight: 600, fontSize: '0.82rem', fontFamily: UI_FONT }}
                >
                  <Ico e="✏" sp />Editar seções
                </button>
              )}
              {!configVisible && (
                <button
                  onClick={handlePrint}
                  disabled={loading || !!error || !reportData}
                  style={{ padding: '0.5rem 1.1rem', background: GOLD, color: '#ffffff', border: 'none',
                    borderRadius: '8px', cursor: loading || error || !reportData ? 'default' : 'pointer',
                    fontWeight: 600, fontSize: '0.85rem', fontFamily: UI_FONT,
                    opacity: loading || error || !reportData ? 0.5 : 1,
                    whiteSpace: 'nowrap' }}
                >
                  <Ico e="🖨" sp />Imprimir / Salvar PDF
                </button>
              )}
              <button
                onClick={onClose}
                style={{ padding: '0.5rem 0.9rem', background: '#f6f0ea', color: '#4a4440',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontWeight: 500, fontSize: '0.85rem', fontFamily: UI_FONT }}
              >
                <Ico e="✕" />
              </button>
            </div>
          </div>

          {/* Conteúdo */}
          <div style={{ overflowY: 'auto', maxHeight: '80vh', background: configVisible ? '#ffffff' : '#fbf8f5' }}>

            {loading && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: '0.75rem', padding: '3rem 0' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%',
                  border: `3px solid ${CREAM2}`, borderTopColor: GOLD,
                  animation: 'spin 0.8s linear infinite' }} />
                <p style={{ margin: 0, color: TEXT_LT, fontSize: '0.87rem', fontFamily: UI_FONT }}>
                  Carregando dados da paciente...
                </p>
              </div>
            )}

            {error && (
              <div style={{ padding: '1.25rem', background: '#f8ebe9', border: '1px solid #f8ebe9',
                borderRadius: '10px', textAlign: 'center', margin: '1rem' }}>
                <p style={{ margin: 0, color: '#a8483f', fontFamily: UI_FONT, fontSize: '0.9rem' }}>
                  <Ico e="⚠" /> {error}
                </p>
              </div>
            )}

            {!loading && !error && reportData && configVisible && (
              <SelectionScreen
                sections={sections}
                onChange={setSections}
                onGerarTudo={handleGerarTudo}
                onGerar={handleGerar}
                reportData={reportData}
                recommendations={recommendations}
                disponivel={{ composicao: (evolution?.length || 0) > 0, medidas: (antropometria?.length || 0) > 0 }}
              />
            )}

            {!loading && !error && reportData && !configVisible && <ReportEmail patient={reportData.patient} />}
            {!loading && !error && reportData && !configVisible && (
              <div style={{ padding: '2rem 2.5rem' }}>
                <div style={{ background: '#ffffff', borderRadius: '8px',
                  boxShadow: '0 2px 16px rgba(0,0,0,0.08)', padding: '2.5rem' }}>
                  <DocumentoContent
                    reportData={reportData} recommendations={recommendations}
                    sections={sections} proximaConsulta={proximaConsulta}
                    evolution={evolution} antropometria={antropometria}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
